<template>
  <k-panel title="Evidence" :hasContentToggle="false">
    <template #body>
      <div class="project-evidence-table">
        <div class="evidence-header">
          <div class="evidence-header-left">
            <p>Add evidence to your project using the 'Add New Evidence' button.</p>
          </div>
          <button class="btn btn-success add-evidence-btn" @click="createNewEvidence">
            Add New Evidence
          </button>
        </div>

        <div class="table-container">
          <k-table v-if="evidenceData.length > 0"
            :headers="tableHeaders"
            :rows="tableRows"
            :hideable="false"
            :downloadable="false"
            :panel="false"
            :max="10"
            @clicked="handleTableAction"
            class="evidence-table"
          />
          <empty-placeholder v-else title="No evidence found" info="You can submit a new evidence by clicking Add button on the right side.">
          </empty-placeholder>
        </div>

        <add-edit-project-evidence
          :show-modal="showEvidenceModal"
          :programme-id="programmeId"
          :project-id="projectId"
          :evidence-id="evidenceId"
          :modal-mode="modalMode"
          :evidence-data="selectedEvidenceData"
          @update="handleEvidenceUpdate"
          @close="resetModalState"
        >
        </add-edit-project-evidence>
      </div>
    </template>
  </k-panel>
</template>
<style scoped>
.project-evidence-table {
  padding: 15px 0;
}

.evidence-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.evidence-header-left p {
  margin: 0;
  color: var(--kate-type-primary, #6c757d);
  font-size: 0.9rem;
}

.table-container {
  overflow-x: auto;
}

.badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 0.25rem;
  margin-right: 0.25rem;
}

.evidence-table .k-table-wrapper {
  border: 1px solid var(--kate-border);
  border-radius: 8px;
  overflow: hidden;
  background-color: var(--kate-background-body);
}

.evidence-table .table {
  margin-bottom: 0;
}

.evidence-table .k-table-header {
  background-color: var(--kate-background-alt);
  font-weight: 600;
  padding: 12px 16px;
  border-bottom: 1px solid var(--kate-border);
  font-size: 14px;
}

.evidence-table .k-table-cell {
  padding: 12px 16px;
  vertical-align: top;
  font-size: 14px;
  border-bottom: 1px solid var(--kate-border-light, #f1f3f4);
}

.evidence-table .k-table-row:last-child .k-table-cell {
  border-bottom: none;
}
</style>
<style>
.evidence-table .badge-success {
  color: #000;
  background-color: var(--kate-success);
}

.evidence-table .badge-secondary {
  color: #000;
  background-color: #f8f9fa;
}

.evidence-action-btn,
.feedback-action-btn {
  border: none !important;
  padding: 0.5rem !important;
  border-radius: 6px !important;
  font-size: 0.875rem !important;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px !important;
  max-width: 40px !important;
  background-color: var(--kate-button-primary) !important;
  color: white !important;
  margin: 0 auto;
  flex-shrink: 0;
}

.evidence-action-btn:hover,
.feedback-action-btn:hover {
  background-color: var(--kate-button-primary-hover) !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.1);
}

.evidence-table .k-table-cell:has(.evidence-action-btn),
.evidence-table .k-table-cell:has(.feedback-action-btn),
.evidence-table .k-table-cell:has(.approval-icon) {
  text-align: center;
  vertical-align: middle;
  width: 80px;
  padding: 8px;
}

.approval-icon {
  font-size: 1.4rem;
  font-weight: bold;
  display: inline-block;
  width: 24px;
  height: 24px;
  text-align: center;
  line-height: 24px;
}

.approval-icon.approved {
  color: var(--kate-success);
}

.approval-icon.neutral {
  color: var(--kate-type-alt) !important;
}

.approval-icon.rejected {
  color: var(--kate-danger, #dc3545);
}

.evidence-table .k-table-cell:has(.approval-icon) {
  text-align: center;
}

.evidence-download-btn i {
  margin-right: 4px;
  font-size: 12px;
}
</style>
<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import AddEditProjectEvidence from './add-edit-project-evidence.vue';

export default {
  name: 'ProjectEvidenceTable',
  components: {
    KPanel,
    KTable,
    EmptyPlaceholder,
    AddEditProjectEvidence,
  },

  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    projectId: {
      type: Number,
      required: true,
    },
    evidenceData: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      showEvidenceModal: false,
      modalMode: undefined, // 'create', 'edit/view'
      evidenceId: undefined,
      selectedEvidenceData: null,
    };
  },

  computed: {
    tableHeaders() {
      return {
        name: {
          name: 'Name',
          type: 'string',
          sortable: true,
          filterable: true,
        },
        description: {
          name: 'Description',
          type: 'string',
          filterable: true,
        },
        competencies: {
          name: 'Competencies',
          type: 'function',
          displayAs: row => {
            if (!row.competencies || row.competencies.length === 0) return '-';
            return row.competencies.map(comp => {
              const badgeClass = comp.status ? 'badge-success' : 'badge-secondary';
              return `<span class="badge ${badgeClass}">${comp.competency_name}</span>`;
            }).join(' ');
          },
        },
        evidenceUploaded: {
          name: 'Evidence Uploaded',
          type: 'action',
          displayAs: evidenceUploaded => {
            const { evidenceLink, signedUrl, fileName } = evidenceUploaded || {};
            if (signedUrl) {
              return `
                <button class="btn btn-primary evidence-download-btn" 
                        title="Download ${fileName || 'file'}">
                  <i class="fas fa-download"></i> Download
                </button>
              `;
            }
            const formattedLink = evidenceLink.startsWith('http://') || evidenceLink.startsWith('https://')
              ? evidenceLink
              : `https://${evidenceLink}`;
            return `<a href="${formattedLink}" class="evidence-link" target="_blank">${evidenceLink}</a>`;
          },
        },
        editEvidence: {
          name: 'Edit Evidence',
          type: 'action',
          displayAs: () => `
            <button class="btn btn-primary evidence-action-btn" title="Edit Evidence">
              <i class="fas fa-edit"></i>
            </button>
          `,
        },
        viewFeedback: {
          name: 'View feedback',
          type: 'action',
          displayAs: () => `
            <button class="btn btn-primary feedback-action-btn" title="View Feedback">
              <i class="fas fa-eye"></i>
            </button>
          `,
        },
        approved: {
          name: 'Approved',
          type: 'function',
          displayAs: row => {
            if (row.approved === true) {
              return '<i class="fas fa-check approval-icon approved"></i>';
            } if (row.approved === null || row.approved === undefined) {
              return '<i class="fas fa-times approval-icon neutral"></i>';
            }
            return '<i class="fas fa-times approval-icon rejected"></i>';
          },
        },
      };
    },

    tableRows() {
      return this.evidenceData.map((evidence, index) => ({
        id: evidence.project_evidence_id,
        name: evidence.name,
        description: evidence.description,
        competencies: evidence.competency_mappings || [],
        evidenceUploaded: {
          evidenceLink: evidence.evidence_link,
          signedUrl: evidence.signed_url,
          fileName: evidence.filename,
        },
        approved: evidence.approved,
        index,
      }));
    },
  },

  methods: {
    resetModalState() {
      this.showEvidenceModal = false;
      this.modalMode = undefined;
      this.evidenceId = undefined;
      this.selectedEvidenceData = null;
    },

    createNewEvidence() {
      this.showEvidenceModal = true;
      this.modalMode = 'create';
      this.selectedEvidenceData = null;
    },

    downloadEvidence(signedUrl) {
      window.open(signedUrl, '_blank');
    },

    handleTableAction(action, index, row, key) {
      if (key === 'editEvidence') {
        const mode = row.approved === true ? 'view' : 'edit';
        this.openEvidenceModal(row.id, mode);
      } else if (key === 'viewFeedback') {
        // TODO: Open View Feedback modal for evidenceId
      } else if (key === 'evidenceUploaded') {
        const signedUrl = row.evidenceUploaded?.signedUrl;
        if (signedUrl) {
          this.downloadEvidence(signedUrl);
        }
      }
    },

    openEvidenceModal(evidenceId, mode) {
      this.evidenceId = evidenceId;
      this.modalMode = mode;

      const evidenceItem = this.evidenceData.find(evidence => evidence.project_evidence_id === evidenceId);
      if (evidenceItem) {
        this.selectedEvidenceData = {
          evidenceId: evidenceItem.project_evidence_id,
          name: evidenceItem.name,
          description: evidenceItem.description,
          competency_mappings: evidenceItem.competency_mappings || [],
          evidence_link: evidenceItem.evidence_link,
          evidence_type: evidenceItem.evidence_type,
          version_id: evidenceItem.version_id,
          file_name: evidenceItem.filename,
          signed_url: evidenceItem.signed_url,
          approved: evidenceItem.approved,
          all_feedback_text: evidenceItem.all_feedback_text,
        };

        this.showEvidenceModal = true;
        this.$logger.info('Evidence modal opened', { evidenceId, mode });
      } else {
        this.$ktoast.warning('Evidence not found');
        this.resetModalState();
      }
    },

    handleEvidenceUpdate() {
      this.resetModalState();
      this.$emit('update-evidence-data');
    },
  },
};
</script>
